import React from "react";
import { DocumentCard,
    DocumentCardDetails,
    DocumentCardTitle,
    DocumentCardActivity,
    DocumentCardLocation } from '@fluentui/react';
import {ApproverDialog} from "./ApproverDialog";
import { PowerAppBaseType, PowerAppChangeTaskType } from '../../GlobalConst/globalEnums';
import { renderDateTime, renderUser } from "../../helpers/HelperMethods";
import Api from '../../services/Api'
import PowerAppOwnerChange from "./ChangeValues/PowerAppOwnerChange";
import PowerAppRecertificationTask from "./ChangeValues/PowerAppRecertificationTask";
import PowerAppDeleteTask from "./ChangeValues/PowerAppDeleteTask";
import DeletePowerAppDialogContent from "../EditSite/PowerAppEditElements/DeletePowerAppDialogContent";

const PowerAppChangeTask = ({change, refresh, getCallback}) => {

    const mapOwnerForPeople = (user) => {
        return user ? [{ name: user.displayName }] : [];
    }

    const onLocationClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        window.open(e.target.href, '_blank');
    }

    const getTitle = (taskType) => {
        switch (taskType) {
            case PowerAppChangeTaskType.primaryOwner:
            case PowerAppChangeTaskType.secondaryOwner:
                return 'Owner change'
            case PowerAppChangeTaskType.recertification:
                return 'Recertification'
            case PowerAppChangeTaskType.delete:
                return 'Deletion request'
            default:
                return null
        }
    }

    const getChangeDetails = (change) => {
        switch (change.type) {
            case PowerAppChangeTaskType.primaryOwner:
            case PowerAppChangeTaskType.secondaryOwner:
                return <PowerAppOwnerChange currentOwner={change.currentValue} newOwner={change.changeValue}/>
            case PowerAppChangeTaskType.recertification:
                return <PowerAppRecertificationTask app={change.powerApp} task={change} getCallback={getCallback} refresh={refresh} />
            case PowerAppChangeTaskType.delete:
                return <PowerAppDeleteTask app={change.powerApp} />
            default:
                return null
        }
    }

    const getDialogTitle = (type) => {
        switch (type) {
            case PowerAppChangeTaskType.primaryOwner:
            case PowerAppChangeTaskType.secondaryOwner:
                return "Approve or reject ownership change"
            case PowerAppChangeTaskType.recertification:
                return "Recertification todo"
            case PowerAppChangeTaskType.delete:
                return "Approve or reject deletion request"
            default:
                return null
        }
    }

    const getObjectType = (app) => {
        switch(app.type) {
            case PowerAppBaseType.flow:
                return 'Power Automate'
            case PowerAppBaseType.agent:
                return 'Agent'
            default:
                return app.appType
        }
    }

    const getDialogMessage = (task) => {
        switch (task.type) {
            case PowerAppChangeTaskType.primaryOwner:
            case PowerAppChangeTaskType.secondaryOwner:
                return task.additionalData === 'True'
                    ? <div>The current owner has requested that ownership be updated <b>only</b> in the Provisioner database. This means that once the transfer is approved, you will <b>not</b> automatically receive access to the artifacts.</div>
                    : <div>The current owner has requested that ownership be updated in <b>both</b> the Provisioner tool and the Power Platform environments. This means that upon approval, you will be granted access to the artifacts in the Power Platform environments.</div>
            case PowerAppChangeTaskType.delete:
                return  <div>
                            <div>Deletion of "{task.powerApp.name}" {task.powerApp.type} has been requested by {renderUser(task.createdBy)}.</div>
                            <DeletePowerAppDialogContent powerApp={task.powerApp} />
                        </div>
            default:
                return null
        }
    }

    const onReject = (comment) => {
        const url = '/RejectPowerAppChange'
        postDecision({comment}, url);
    }

    const onApprove = (approveObject) => {
        const url = '/ApprovePowerAppChange';
        postDecision(approveObject, url);
    }

    const postDecision = (approveObject, urlPart) => {
        const post = {
            id: change.id,
            ...approveObject
        };

        Api.post(urlPart, post)
            .then(res => res.data)
            .then(post => {
                    if (post === 200) {
                        refresh();
                    } else {
                        console.log("Update failed")
                    }
                }
            ).catch((error) => {
            console.log(error)})
    }

    const borderColor = (app) => {
        switch (app.type)
        {
            case PowerAppBaseType.powerApp:
                return 'rgb(116, 39, 116)'
            case PowerAppBaseType.flow:
                return 'rgb(0, 102, 255)'
            default:
                return 'rgb(138, 136, 134)'
        }
    }

     const logoClass = (type) => {
        switch (type) {
            case PowerAppBaseType.powerApp:
                return 'powerAppLogo'
            case PowerAppBaseType.flow:
                return 'flowLogo'
            case PowerAppBaseType.agent:
                return 'copilotLogo'
            default:
                return ''
        }
    }
    const logo = <span className={`cardLogo ${logoClass(change.powerApp.type)}`} />

    return (
        <div>
            <DocumentCard  styles={cardStyles(borderColor(change.powerApp))} >
                <DocumentCardDetails styles={detailsStyles} >
                    <div style={headerStyles}>
                        {logo}
                        <div style={titleStyles}>
                            <DocumentCardTitle title={getTitle(change.type)} shouldTruncate={false} styles={cardTitleStyles} />
                            <div style={objectTypeStyles}>
                            {
                                getObjectType(change.powerApp)
                            }
                            </div>
                        </div>
                    </div>
                    <DocumentCardLocation 
                        styles={urlStyles} 
                        location={change.powerApp.name} 
                        locationHref={change.powerApp.url} 
                        onClick={onLocationClick} 
                    />
                    {
                        getChangeDetails(change)
                    }
                </DocumentCardDetails>
                {
                    change.type !== PowerAppChangeTaskType.recertification &&
                    <div>
                        <div style={createdByStyles}>
                            <div>Change requested by: </div>
                            <DocumentCardActivity
                                activity={`Created on ${renderDateTime(change.requestDate)}`}
                                people={mapOwnerForPeople(change.createdBy)}
                            />
                        </div>
                        <div style={iconContainerStyles}> 
                            <ApproverDialog 
                                buttonName={"Decision"}
                                title={getDialogTitle(change.type)}
                                onReject={onReject}
                                onApprove={onApprove}
                                additionalInfo={getDialogMessage(change)}
                                approveBtnText={change.type === PowerAppChangeTaskType.delete && 'Approve deletion'}
                                rejectBtnText={change.type === PowerAppChangeTaskType.delete && 'Block deletion'}
                            />
                        </div>
                    </div>
                }
            </DocumentCard>
        </div>
    );
}

const urlStyles = {
    root: {
        color: 'rgb(0, 120, 212)',
        fontSize: '14px',
        // fontWeight: 'bold',
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        padding: '20px 12px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }
}

const createdByStyles = {
    padding: '8px',
    fontSize: '12px',
    flex: 1
}

const iconContainerStyles = {
    display: 'flex',
    flexDirection: 'column'
}
const titleStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    maxHeight: '35px',
    alignItems: 'baseline',
    flex: 1
}

const cardStyles = (borderColor) => ({
    root: {  
        marginRight: 20,
        marginBottom: 20,
        width: 400,
        height: 300,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid ' + borderColor
    }
})

const detailsStyles = {
    root: {
        height: 235,
        flex: 5,
        justifyContent: 'start'
    }
}

const objectTypeStyles = {
    padding: '8px 16px 8px 8px',
    fontSize: 14,
    textAlign: 'right'
}

const headerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
}

const cardTitleStyles = {
    root: {
        padding: '8px',
        minWidth: '145px'
    }
}

export default PowerAppChangeTask


// WEBPACK FOOTER //
// ./src/components/ManageCenter/PowerAppChangeTask.js